import { CrlItemStatus, CrlRequestItem } from '../../models';
import {
  CrlDisplaySection,
  CRL_FILTER_LABELS,
  CRL_FILTER_ORDER,
  CRL_FILTER_STATUS,
  CRL_FILTER_SUBSUMES,
  CrlFilterKey,
  CrlFilterOption,
  CrlFilterSelection,
  CrlSectionFilter,
  CrlSectionOption,
} from '../../models';

/**
 * Pure CRL filter + search helpers (no DI, no state lookups). The display
 * sections come from `CxpCrlDetailSelectors.getSelectedRequestDisplay` — already
 * split into open/finished and ordered with completed sections last — and the
 * helpers narrow that projection. Mirrors `cxp-task-filters.utils` so the new
 * page composes filtering through the facade in a single line.
 *
 * The seven REQ-12 buckets are OR-combined: an item passes the filter step when
 * it matches ANY selected bucket. Search (title + description, case-insensitive)
 * is then AND-ed on top. The selector keeps owning the open/finished split and
 * completion ordering — these helpers only drop non-matching items/sections.
 */

/** True when the item belongs to the given filter bucket. */
function itemMatchesFilter(item: CrlRequestItem, key: CrlFilterKey, viewerContactIds: ReadonlySet<number>): boolean {
  switch (key) {
    // Spans two statuses so the bucket counts what its tile counts: an overdue
    // item is a past-due WAITING item, and `deriveItemStatus` replaces the
    // status rather than adding to it.
    case CrlFilterKey.WAITING_FOR_YOU:
      return item.status === CrlItemStatus.WAITING || item.status === CrlItemStatus.OVERDUE;
    case CrlFilterKey.OVERDUE:
      return item.status === CrlItemStatus.OVERDUE;
    case CrlFilterKey.ASSIGNED_TO_ME:
      return item.assignees.some((assignee) => assignee.contactId != null && viewerContactIds.has(assignee.contactId));
    default: {
      const status = CRL_FILTER_STATUS[key];
      return status !== undefined && item.status === status;
    }
  }
}

/** True when the item passes the active selection (OR across buckets; empty = pass). */
function itemMatchesSelection(item: CrlRequestItem, selection: CrlFilterSelection, viewerContactIds: ReadonlySet<number>): boolean {
  if (selection.size === 0) return true;
  for (const key of selection) {
    if (itemMatchesFilter(item, key, viewerContactIds)) return true;
  }
  return false;
}

function itemMatchesQuery(item: CrlRequestItem, normalizedQuery: string): boolean {
  return item.name.toLowerCase().includes(normalizedQuery) || item.description.toLowerCase().includes(normalizedQuery);
}

function itemPasses(
  item: CrlRequestItem,
  selection: CrlFilterSelection,
  normalizedQuery: string,
  viewerContactIds: ReadonlySet<number>,
): boolean {
  if (!itemMatchesSelection(item, selection, viewerContactIds)) return false;
  if (normalizedQuery.length > 0 && !itemMatchesQuery(item, normalizedQuery)) return false;
  return true;
}

/**
 * Applies the section scope + active filter selection + search query to the
 * projected display sections. The section scope (PP-3642) narrows to a single
 * section first; the filter buckets + search then narrow items within the
 * surviving section(s). Keeps a section only when it still has a matching open
 * OR finished item, preserving the selector's open/finished split and completion
 * ordering. The completion flag is recomputed against the surviving items so a
 * section filtered down to only finished items still renders below the divider.
 *
 * Fast path: no section scope, no filters, and no search returns the original
 * reference so OnPush change detection sees identity equality.
 */
export function applyCrlFilters(
  sections: CrlDisplaySection[],
  selection: CrlFilterSelection,
  searchQuery: string,
  viewerContactIds: ReadonlySet<number>,
  sectionFilter: CrlSectionFilter = null,
): CrlDisplaySection[] {
  const normalizedQuery = searchQuery.trim().toLowerCase();
  if (sectionFilter === null && selection.size === 0 && normalizedQuery.length === 0) {
    return sections;
  }

  const scoped = sectionFilter === null ? sections : sections.filter((section) => section.id === sectionFilter);

  const result: CrlDisplaySection[] = [];
  for (const section of scoped) {
    const openItems = section.openItems.filter((item) => itemPasses(item, selection, normalizedQuery, viewerContactIds));
    const finishedItems = section.finishedItems.filter((item) => itemPasses(item, selection, normalizedQuery, viewerContactIds));
    if (openItems.length === 0 && finishedItems.length === 0) continue;
    result.push({
      ...section,
      openItems,
      finishedItems,
      isComplete: openItems.length === 0,
    });
  }
  return result;
}

/**
 * Builds the "All Sections" dropdown options (PP-3642): a leading "All Sections"
 * reset entry (`id: null`) followed by one option per section, labelled with its
 * letter identifier + name (e.g. `A. Income`). Derived from the SAME projected
 * display sections the list renders, so the order matches (completed sections
 * last). Sections are matched by id, so the scope survives re-projection.
 */
export function buildCrlSectionOptions(sections: CrlDisplaySection[]): CrlSectionOption[] {
  return [
    { id: null, label: 'All Sections' },
    ...sections.map((section) => ({ id: section.id, label: `${section.identifier}. ${section.name}` })),
  ];
}

/**
 * Builds the seven multi-select filter options with per-bucket counts. Counts
 * reflect the FULL request (every item across all sections) — they summarize,
 * they do not react to the current selection or search, so the option set never
 * collapses as the user filters (same rule as the tasks sidebar chips).
 */
export function buildCrlFilterOptions(
  sections: CrlDisplaySection[],
  selection: CrlFilterSelection,
  viewerContactIds: ReadonlySet<number>,
): CrlFilterOption[] {
  const items = sections.flatMap((section) => [...section.openItems, ...section.finishedItems]);
  return CRL_FILTER_ORDER.map((key) => ({
    key,
    label: CRL_FILTER_LABELS[key],
    count: items.filter((item) => itemMatchesFilter(item, key, viewerContactIds)).length,
    selected: selection.has(key),
  }));
}

/**
 * True when every item in `key`'s bucket is already in view — either because
 * `key` itself is selected, or because a selected bucket contains it
 * ({@link CRL_FILTER_SUBSUMES}). Selecting a covered key would widen nothing.
 */
export function isCrlFilterCovered(selection: CrlFilterSelection, key: CrlFilterKey): boolean {
  if (selection.has(key)) return true;
  for (const active of selection) {
    if (CRL_FILTER_SUBSUMES[active]?.includes(key)) return true;
  }
  return false;
}

/** Adds `key`, dropping the buckets it makes redundant. */
function selectCrlFilter(selection: CrlFilterSelection, key: CrlFilterKey): Set<CrlFilterKey> {
  const next = new Set(selection);
  next.add(key);
  for (const subsumed of CRL_FILTER_SUBSUMES[key] ?? []) next.delete(subsumed);
  return next;
}

/**
 * Toggles a single bucket. Selecting one replaces the narrower buckets it
 * contains, so clicking Waiting for You while Overdue is active leaves Waiting
 * for You alone rather than two chips describing one list.
 */
export function toggleCrlFilter(selection: CrlFilterSelection, key: CrlFilterKey): CrlFilterSelection {
  if (!selection.has(key)) return selectCrlFilter(selection, key);
  const next = new Set(selection);
  next.delete(key);
  return next;
}

/** What makes a preset's control read as ON. */
export function isCrlPresetActive(selection: CrlFilterSelection, preset: readonly CrlFilterKey[]): boolean {
  return preset.length > 0 && preset.every((key) => isCrlFilterCovered(selection, key));
}

/**
 * Turning on adds only the buckets not already covered; turning off removes the
 * preset's own buckets and nothing else, so a wider bucket the user selected
 * themselves — Waiting for You from its tile — survives.
 */
export function toggleCrlPreset(selection: CrlFilterSelection, preset: readonly CrlFilterKey[]): CrlFilterSelection {
  if (isCrlPresetActive(selection, preset)) {
    const next = new Set(selection);
    for (const key of preset) next.delete(key);
    return next;
  }
  let next: CrlFilterSelection = selection;
  for (const key of preset) {
    if (!isCrlFilterCovered(next, key)) next = selectCrlFilter(next, key);
  }
  return next;
}

/** Serializes the selection set to the persisted array shape. */
export function serializeCrlSelection(selection: CrlFilterSelection): CrlFilterKey[] {
  return [...selection];
}

/** All valid filter keys, for parse-guard validation. */
const VALID_FILTER_KEYS: ReadonlySet<string> = new Set<string>(CRL_FILTER_ORDER);

/**
 * Parses a persisted payload back into a selection set, discarding anything that
 * isn't a known `CrlFilterKey`. Tolerant by design: a non-array, a renamed key,
 * or junk yields an empty selection rather than throwing — the migration guard
 * for a key/shape change (see `CRL_FILTERS_STORAGE_KEY`).
 */
export function parseCrlSelection(raw: unknown): CrlFilterSelection {
  if (!Array.isArray(raw)) return new Set();
  const keys = raw.filter((value): value is CrlFilterKey => typeof value === 'string' && VALID_FILTER_KEYS.has(value));
  return new Set(keys);
}
